import type { DocContent, Patient } from "../types";

/** Часовой пояс системы — Москва: все «сейчас» и «сегодня» считаются по нему. */
export const APP_TZ = "Europe/Moscow";

const MSK_FMT = new Intl.DateTimeFormat("en-GB", {
  timeZone: APP_TZ,
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
  hour12: false,
});

/**
 * Текущий момент по Москве — как Date, чьи «локальные» поля (getHours и т. д.)
 * равны московскому времени, в каком бы поясе ни был браузер. Именно такой
 * Date ждут phpDate и всё, что печатает дату/время «сейчас».
 */
export function nowMsk(): Date {
  return mskDate(new Date());
}

/** Тот же момент, но в московских «локальных» полях — для печати отметок времени. */
export function mskDate(at: Date): Date {
  const p: Record<string, number> = {};
  for (const part of MSK_FMT.formatToParts(at)) {
    if (part.type !== "literal") p[part.type] = Number(part.value);
  }
  return new Date(p.year, p.month - 1, p.day, p.hour % 24, p.minute, p.second);
}

/** Сегодня по Москве, «YYYY-MM-DD». */
export function todayIso(): string {
  return phpDate("Y-m-d", nowMsk());
}

/** Текущее московское время «ЧЧ:ММ». */
export function nowHM(): string {
  return phpDate("H:i", nowMsk());
}

/**
 * A tiny subset of PHP's date() — only the format strings the document
 * templates actually use: "d.m.Y", "Y", "Y-m-d", "d.m.Y, H:i", "d.m.Y H:i", "H:i", "n", "j".
 */
export function phpDate(
  format: string,
  date: Date | string | null = nowMsk(),
): string {
  const d = toDate(date);
  if (!d) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  let out = "";
  for (const ch of format) {
    switch (ch) {
      case "d":
        out += pad(d.getDate());
        break;
      case "j":
        out += String(d.getDate());
        break;
      case "m":
        out += pad(d.getMonth() + 1);
        break;
      case "n":
        out += String(d.getMonth() + 1);
        break;
      case "Y":
        out += String(d.getFullYear());
        break;
      case "y":
        out += pad(d.getFullYear() % 100);
        break;
      case "H":
        out += pad(d.getHours());
        break;
      case "i":
        out += pad(d.getMinutes());
        break;
      case "s":
        out += pad(d.getSeconds());
        break;
      default:
        out += ch;
    }
  }
  return out;
}

/** Accepts the "YYYY-MM-DD" / "YYYY-MM-DD HH:MM:SS" strings the API returns. */
export function toDate(value: Date | string | null | undefined): Date | null {
  if (!value) return null;
  if (value instanceof Date)
    return Number.isNaN(value.getTime()) ? null : value;
  const s = String(value).trim();
  if (!s) return null;
  const m =
    /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2})(?::(\d{2}))?)?/.exec(s);
  if (m) {
    return new Date(
      +m[1],
      +m[2] - 1,
      +m[3],
      +(m[4] ?? 0),
      +(m[5] ?? 0),
      +(m[6] ?? 0),
    );
  }
  const dmy =
    /^(\d{1,2})\.(\d{1,2})\.(\d{4})(?:[,\s—-]+(\d{1,2}):(\d{2}))?/.exec(s);
  if (dmy) {
    return new Date(
      +dmy[3],
      +dmy[2] - 1,
      +dmy[1],
      +(dmy[4] ?? 0),
      +(dmy[5] ?? 0),
    );
  }
  return null;
}

/** "2026-08-18" -> "18.08.2026"; empty for null. */
export const ru = (value: string | null | undefined) =>
  phpDate("d.m.Y", value ?? null);
/** "2026-08-18 16:24:00" -> "18.08.2026 в 16:24" — the case-panel wording. */
export const ruAt = (value: string | null | undefined) => {
  const d = toDate(value);
  return d ? `${phpDate("d.m.Y", d)} в ${phpDate("H:i", d)}` : "";
};
export const ruDateTime = (value: string | null | undefined) =>
  phpDate("d.m.Y, H:i", value ?? null);
export const year = (value: string | null | undefined) =>
  phpDate("Y", value ?? null);

/** Port of initials() in inc/functions.php — "Иванов И. И." */
export function initials(name: string | null | undefined): string {
  const parts = String(name ?? "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (parts.length === 0) return "";
  if (parts.length === 1) return parts[0];
  if (parts.length === 2) return `${parts[0]} ${parts[1][0]}.`;
  return `${parts[0]} ${parts[1][0]}. ${parts[2][0]}.`;
}

export function clearPhone(phone: string | null | undefined): string {
  let p = String(phone ?? "");
  if (p.startsWith("8")) p = "7" + p.slice(1);
  return p.replace(/\D/g, "");
}

export function formatPhone(phone: string | null | undefined): string {
  const d = clearPhone(phone);
  if (d.length < 11) return String(phone ?? "");
  return `+${d.slice(0, 1)} (${d.slice(1, 4)}) ${d.slice(4, 7)}-${d.slice(7, 9)}-${d.slice(9, 11)}`;
}

/**
 * The expression helpers a converted template's `def` callback receives. They
 * stand in for the PHP superglobals the old templates read directly: $pat,
 * $case, and the $perv / $oper / $karta / $dnev prefill objects.
 */
export interface DocHelpers {
  mode: "add" | "edit" | "temp";
  pat: Patient;
  /** The case row, loosely typed: templates read whichever column they need. */
  cs: Record<string, any> | null;
  /** Latest value of `field` in the newest `docsId` document of this case. */
  src: (docsId: string, field: string) => string;
  /** php date() against "now" or a given value. */
  date: (format: string, value?: Date | string | null) => string;
  year: (value: string | null | undefined) => string;
  initials: typeof initials;
  /** "Иванов Иван Иванович, 1964 г.р." — repeated verbatim across templates. */
  patFioYear: () => string;
  /** Whole years between a date and today: floor((time() - $bdate) / 31536000). */
  age: (value: string | null | undefined) => number;
  /** Whole days from `from` to `to`, rounded up: ceil((b - a) / 86400). */
  days: (
    from: string | null | undefined,
    to: string | null | undefined,
  ) => number;
}

export function makeHelpers(
  mode: "add" | "edit" | "temp",
  pat: Patient,
  cs: Record<string, any> | null,
  sources: Record<string, DocContent | null>,
): DocHelpers {
  return {
    mode,
    pat,
    cs,
    src: (docsId, field) => sources[docsId]?.[field] ?? "",
    date: (format, value = nowMsk()) => phpDate(format, value ?? nowMsk()),
    year,
    initials,
    patFioYear: () => {
      const y = year(pat.bdate);
      return y ? `${pat.fio}, ${y} г.р.` : pat.fio;
    },
    // PHP divided a unix delta by 31536000 (365 days), so a birthday in a leap
    // year could read a year young. Counted on the calendar instead.
    age: (value) => {
      const d = toDate(value ?? null);
      if (!d) return 0;
      const now = nowMsk();
      let years = now.getFullYear() - d.getFullYear();
      const before =
        now.getMonth() < d.getMonth() ||
        (now.getMonth() === d.getMonth() && now.getDate() < d.getDate());
      if (before) years--;
      return Math.max(0, years);
    },
    days: (from, to) => {
      const a = toDate(from ?? null);
      const b = toDate(to ?? null);
      if (!a || !b) return 0;
      return Math.ceil((b.getTime() - a.getTime()) / 86400000);
    },
  };
}

/**
 * Сумма из поля: «1 500», «12500,50», «3 000 ₽» → число; пустое — 0; не
 * число — null. Number("1 500") — NaN, и сумма молча становилась нулём.
 */
export function parseMoney(text: string): number | null {
  const s = String(text ?? "")
    .replace(/[\s ₽]/g, "")
    .replace(/руб\.?/i, "")
    .replace(",", ".");
  if (!s) return 0;
  const n = Number(s);
  return Number.isFinite(n) && n >= 0 ? n : null;
}

/**
 * Занятость из карточки пациента (там свободный текст: «Пенсионер»,
 * «работает», «не работает»; у пенсионера и «Место работы» нередко
 * «пенсионер(ка)») — код в строке «Занятость» «Сведений» и статкарты; не
 * распознана — пусто, врач подчеркнёт сам.
 */
export function occupationCode(text: string | null | undefined): string {
  const t = String(text ?? "").toLowerCase();
  if (/не\s*работ|безработ/.test(t)) return "5";
  // «Работающий пенсионер» — работает.
  if (/работа(ет|ющ)/.test(t)) return "1";
  if (/пенсион/.test(t)) return "3";
  if (/военн|воинск/.test(t)) return "2";
  if (/служащ|рабоч/.test(t)) return "1";
  if (/школьн/.test(t)) return "9";
  if (/студент|учащ|обучающ|курсант/.test(t)) return "4";
  return "";
}
